import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Award, Plus, Trash2 } from "lucide-react";
import type { RequestState } from "@redux/state/shared-types";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { PDF_FILETYPE, PDF_MAX_FILE_SIZE } from "@shared-kernel/domain/types/file-validation";
import { TeachingMethodologiesField } from "@shared-kernel/primary/shared/teaching-methodologies-field/teaching-methodologies-field";

export type ResumeField = "teacherResumeUrl" | "artistResumeUrl";

const CertificationsField = () => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: "certifications", keyName: "fieldId" });

  return (
    <FormField
      name="certifications"
      render={() => (
        <FormItem>
          <FormLabel>Certifications et diplômes</FormLabel>
          <FormDescription>
            Toute reconnaissance formelle de votre parcours — académique, professionnelle, institutionnelle, … Elle figure possiblement déjà
            dans votre bio/CV mais elle doit être reprise ici pour permettre aux élèves de filtrer leurs recherches.
          </FormDescription>
          <div className="space-y-3">
            {fields.map((field, rowIndex) => (
              <div
                key={(field as { fieldId: string }).fieldId}
                className="flex items-center gap-3 rounded-md border p-3"
                data-testid={`certification-row-${rowIndex}`}
              >
                <Award className="size-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 space-y-1">
                  <div className="font-medium">Certification {rowIndex + 1}</div>
                  <FormField
                    name={`certifications.${rowIndex}`}
                    render={({ field: itemField }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...itemField}
                            value={itemField.value ?? ""}
                            placeholder="Intitulé de la certification"
                            data-testid={`certification-input-${rowIndex}`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(rowIndex)}
                  className="size-8"
                  data-testid={`certification-remove-${rowIndex}`}
                >
                  <Trash2 className="size-4" color="red" />
                  <span className="sr-only">Supprimer cette certification</span>
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="w-full" onClick={() => append("")} data-testid="certification-add">
              <Plus className="size-4" />
              Ajouter une certification
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface ResumeUploadProps {
  name: ResumeField;
  label: string;
  description: string;
  required: boolean;
  isUploading: boolean;
  onUpload: (field: ResumeField, file: File) => void;
}

const ResumeUploadField = ({ name, label, description, required, isUploading, onUpload }: ResumeUploadProps) => {
  const { setValue, setError, clearErrors } = useFormContext();

  return (
    <FormField
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem>
          <FormLabel>{`${label}${required ? "" : " (optionnel)"}`}</FormLabel>
          <FormDescription>{description}</FormDescription>
          <UploadButton
            processing={isUploading ? "pending" : "idle"}
            buttonText="Télécharger un document (pdf)"
            onFileSelect={file => {
              clearErrors(name);
              onUpload(name, file);
            }}
            onError={() => setError(name, { type: "manual", message: "Veuillez télécharger un fichier PDF de 10 Mo max" })}
            accept={PDF_FILETYPE}
            maxSize={PDF_MAX_FILE_SIZE}
            value={field.value || null}
            onFileDelete={() => {
              clearErrors(name);
              setValue(name, "", { shouldDirty: true });
            }}
            helpText="Télécharger un document PDF de 10 Mo max"
            error={error?.message}
            data-testid={`${name}-upload`}
          />
        </FormItem>
      )}
    />
  );
};

interface Props {
  // Téléversement d'un CV : le hook pose l'issue sur le champ
  onUpload: (field: ResumeField, file: File) => void;
  // Téléversement du document d'une méthodologie : le hook pose l'issue sur la ligne
  onUploadMethodology: (rowIndex: number, file: File) => void;
  uploadProcessing: RequestState;
}

export const TeacherPedagogySubform = ({ onUpload, onUploadMethodology, uploadProcessing }: Props) => {
  // Dernier fichier choisi (un CV ou une méthodologie) : lui seul porte le spinner tant que le téléversement est en cours
  const [selectedResumeField, setSelectedResumeField] = useState<ResumeField | "methodology" | null>(null);
  const uploadingResumeField = uploadProcessing === "pending" ? selectedResumeField : null;

  const handleResumeUpload = (field: ResumeField, file: File) => {
    setSelectedResumeField(field);
    onUpload(field, file);
  };

  const handleMethodologyUpload = (rowIndex: number, file: File) => {
    setSelectedResumeField("methodology");
    onUploadMethodology(rowIndex, file);
  };

  return (
    <div className="space-y-6">
      <ResumeUploadField
        name="teacherResumeUrl"
        label="CV pédagogique"
        description="Votre document de référence en tant qu'enseignant. PDF, 10 Mo max."
        required
        isUploading={uploadingResumeField === "teacherResumeUrl"}
        onUpload={handleResumeUpload}
      />
      <ResumeUploadField
        name="artistResumeUrl"
        label="CV artistique"
        description="Votre biographie ou CV artistique, en complément (optionnel). Il permet de valoriser votre parcours, mais reste secondaire par rapport au CV pédagogique. PDF, 10 Mo max."
        required={false}
        isUploading={uploadingResumeField === "artistResumeUrl"}
        onUpload={handleResumeUpload}
      />
      <CertificationsField />
      <TeachingMethodologiesField
        name="teachingMethodologies"
        onUpload={handleMethodologyUpload}
        uploadProcessing={selectedResumeField === "methodology" ? uploadProcessing : "idle"}
      />
    </div>
  );
};
